// The words on the currency-conversion box of the expense form, and on the
// expense list beside an expense with no US-dollar amount. (ZEN-555)
//
// EVERY SENTENCE HERE IS MARYSE'S. Approved 2026-10-05 in the ZEN-555 session
// (headings A1-A4, "no rate yet" B) and 2026-09-20 on ZEN-680 (the amber line
// and the tip for a currency no source publishes, C). The four marked
// APPROVED 2026-10-14 she approved as written in the ZEN-555 session on 14
// October 2026 ("All four wordings approved as written"). No sentence is to be reworded
// here without her say-so — if one will not fit, ask.
//
// One file so the form and the list cannot drift into two versions of one
// sentence, and so the rules that choose a sentence can be tested without
// rendering the form.
import { fmtDate } from "@/lib/format";
import { apiErr } from "@/lib/api";
import { currencyName } from "@/lib/currencies";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "Fri 04/Sep/2026" — the weekday, then the date in the family's own format.
 * The weekday is the point of heading A2: it is what shows a Sunday payment
 * was priced from the Friday before. */
export const dayWithWeekday = (iso) => {
  const m = String(iso || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(iso || "");
  const wd = WEEKDAYS[new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay()];
  return `${wd} ${fmtDate(m[0])}`;
};

/** The quarter-end that covers a payment day: the latest 31 Mar, 30 Jun,
 * 30 Sep or 31 Dec on or before it. Same rule as the server's. */
export const coveringQuarterEnd = (iso) => {
  const day = String(iso || "").slice(0, 10);
  const y = parseInt(day.slice(0, 4), 10);
  if (!y) return null;
  const ends = [y - 1, y].flatMap((yy) => [`${yy}-03-31`, `${yy}-06-30`, `${yy}-09-30`, `${yy}-12-31`]);
  return ends.filter((e) => e <= day).pop() || null;
};

/** What follows "Currency conversion" in the box's heading.
 *
 * `fx` is the rate in hand: { rate, rate_date, source, final }.
 *   - for a saved expense, its stored rate (final = not an estimate);
 *   - while typing, the server's preview for that currency and day.
 * `waiting` — no rate yet, but a source publishes this currency.
 * `manual`  — the rate was typed by hand.
 */
export function fxHeading({ fx, payDay, waiting = false, manual = false }) {
  if (manual) return "— rate entered by hand, locked";                       // APPROVED 2026-10-14
  if (waiting) return "— awaiting a rate";                                   // APPROVED 2026-10-14
  if (!fx || !fx.rate_date) return "";
  const rated = String(fx.rate_date).slice(0, 10);
  const day = String(payDay || "").slice(0, 10);
  const estimate = fx.final === false;
  if (fx.source === "treasury") {
    const quarter = `US Treasury rate for the quarter ending ${fmtDate(rated)}`;
    return estimate
      ? `— ${quarter}, an estimate until the rate for the transaction date's quarter is published`   // APPROVED 2026-10-14
      : `— ${quarter}, locked`;                                              // A4
  }
  if (estimate) {
    // A3
    return `— European Central Bank rate of ${dayWithWeekday(rated)}, an estimate until the rate for the transaction date is published`;
  }
  if (rated === day) return "— European Central Bank rate for the transaction date, locked";   // A1
  // A2
  return `— European Central Bank rate of ${dayWithWeekday(rated)}, the last published before the transaction date, locked`;
}

// B — no rate yet, but one will come.
export const NO_RATE_YET =
  "No exchange rate is available for the transaction date yet. This expense has no US-dollar amount and is not counted in any total until one arrives; it will fill itself in then.";
export const AWAITING_A_RATE_BADGE = "awaiting a rate";
export const AWAITING_A_RATE_HOVER =
  "No exchange rate is available yet, so this expense has no US-dollar amount and is not counted in any total. It fills itself in once a rate arrives.";

// C — no rate will ever come. ZEN-774: the one shared sentence; every screen uses this.
export const noAutomaticRateFor = (currency) =>
  `No automatic rate for ${currencyName(currency)} — enter the rate from your statement or another reliable source.`;
// ZEN-770: a rate a source publishes, not fetched just now (owner, 16 Oct 2026).
export const rateNotFetchedYet = (date) =>
  `The exchange rate for ${fmtDate(date)} could not be fetched just now. Try again in a few minutes.`;
// A1 (owner, 16 Oct 2026): a typed rate that is not a plain number above zero.
export const BAD_TYPED_RATE =
  "The exchange rate must be a number greater than zero. Enter the rate from your statement or another reliable source.";
// A typed rate as a number, or null: digits with at most one decimal point, above zero.
// Anything else ("abc", "1,3", "1e5", "", "0", "-1") is never turned into a number.
export const typedRateValue = (text) => {
  const t = String(text ?? "");
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : null;
};
// A refusal's sentence; a pending rate's date in the app's own date format.
export const rateRefusal = (e) => {
  const d = e?.response?.data?.detail;
  return d?.rate_pending ? rateNotFetchedYet(d.date) : apiErr(e);
};
// Under an estimated rate on the expense form: what waits for the final rate,
// and how to finish it now. The server refuses both until then (ZEN-834).
// Wording approved by Maryse, 2 Nov 2026 (ZEN-334).
export const ESTIMATE_RATE_NOTE =
  "This exchange rate is an estimate until the official rate for this date is in. " +
  "Until then, this expense can't be matched to a 529 withdrawal or reserved for the AOTC. " +
  "If you know the exact rate, enter it by hand and it counts as final.";

export const CONVERSION_BOX_TIP =
  "We convert using European Central Bank and US Treasury rates only. When neither publishes a rate for the currency you chose, the app cannot work out the US dollar amount, and Manual override switches on by itself. Enter the rate from a reliable source or your statement, dated the day of the transaction - or the closest earlier day if that was a weekend or holiday.";
export const NEEDS_A_RATE_BADGE = "needs a rate";
export const NEEDS_A_RATE_HOVER =
  "No exchange rate was available, so this has no US-dollar amount and is not counted in any total. Edit the expense and enter a rate.";

// The count above the expense list, and the chip while its filter is on. Some
// of these expenses fill themselves in and some need a typed rate, so neither
// "needs a rate" nor "awaiting a rate" is true of all of them.
export const notInTheseTotals = (n) => `${n} not in these totals — no US-dollar amount yet`;   // APPROVED 2026-10-14
export const NO_DOLLAR_AMOUNT_FILTER = "No US-dollar amount yet";                              // APPROVED 2026-10-14

/** Is this expense waiting for a rate that will come (B), as opposed to
 * needing one typed (C)? The server marks a waiting expense as an estimate
 * with no dollar figure; one that needs a typed rate is not an estimate. */
export const isAwaitingARate = (e, unpriced) => !!unpriced && e?.is_fx_estimate === true;
